import { ApplicationController } from '@aha-app/mvc';
import { sortBy } from 'lodash';
import HasGlobalContainer, {
  type IHasGlobalContainer,
} from 'components/framework/controllers/concerns/HasGlobalContainer';
import CardLayoutEditor from '../CardLayoutEditor';
import CardStyling from 'components/framework/models/CardStyling';
import CustomField from '../models/CustomField';
import AhaAnalytics from 'javascripts/analytics';
import { buildCustomField, buildCustomFieldSet } from '../models/custom_fields';
import { randomId } from 'javascripts/utils';
import {
  CardFieldTypesEnum,
  CardStylingTypesEnum,
} from 'components/framework/Enums';
import { sharedSelectionController } from 'components/SelectionController/SelectionController';
import {
  type BookmarkLayoutEditorSettings,
  type CardLayoutEditorControllerLocalState,
  type CardLayoutEditorControllerState,
  type UiItem,
} from './types';
import {
  CardLayout,
  CardField,
  CardRow,
  Project,
  ProjectTemplate,
  ProjectConfiguration,
  ProjectScreens,
  Bookmark,
  type ScreenDefinition,
  Whiteboard,
} from 'components/framework/models';
import type RecordFields from '../models/RecordFields';
import { type CardLayoutModelConstructorType } from '../models/types';
import { type Subqueries } from 'components/framework/Query';
import {
  type CardViewType,
  type RecordId,
  type WhiteboardCardLayoutEditorOptions,
} from 'javascripts/shared-types';
import {
  ALL_CARD_VIEWS,
  DEFAULT_CARD_VIEWS,
} from 'components/framework/models/RecordLayout';
import { showAlertMessage } from 'javascripts/messages';
import FieldSet from '../models/FieldSet';

// Ensure all RecordFields classes are loaded
require('../models/record_fields/all');

const UI_ITEMS: UiItem[] = [{ id: 'DividerRow', title: 'Divider line' }];

interface CardLayoutEditorController extends IHasGlobalContainer {}

@HasGlobalContainer(CardLayoutEditor)
class CardLayoutEditorController extends ApplicationController<CardLayoutEditorControllerState> {
  static initialState: CardLayoutEditorControllerState = {
    saving: false,
    availableRecordLayouts: [],
    cardLayout: null,
    activeCardView: 'expanded',
    cardViews: DEFAULT_CARD_VIEWS,
    currentRecordLayoutKey: null,
    open: false,
    projectScreens: undefined,
    showCustomFields: true,
    showStandardFields: true,
    showUiItems: true,
    visibleStandardFields: [],
    visibleCustomFields: [],
    visibleCustomFieldSets: [],
    visibleUiItems: [...UI_ITEMS],
    visibleCardViews: ['expanded', 'collapsed'],
    // Some options supported by server-side rendering are not yet supported in MVC/GQL rendering
    hideRailsRenderingOptions: false,
    hideUnsupportedCustomFields: false,
  };

  local: CardLayoutEditorControllerLocalState;

  async initialize() {
    this.local = {
      callback: cardLayout => {},
      customFields: {},
      customFieldSets: {},
      defaultCardLayout: null,
    };
  }

  get currentRecordLayoutKey() {
    return (
      this.state.currentRecordLayoutKey || this.state.availableRecordLayouts[0]
    );
  }

  get currentRecordLayout() {
    return this.state.cardLayout[this.currentRecordLayoutKey];
  }

  get cardViewKey() {
    return this.state.activeCardView || 'expanded';
  }

  get currentCardView() {
    return this.currentRecordLayout[this.cardViewKey];
  }

  get currentRecordFields(): RecordFields {
    return this.currentRecordLayout.recordFields;
  }

  get currentModelClass(): CardLayoutModelConstructorType {
    return this.currentRecordFields.modelClass;
  }

  get currentModelTypename() {
    return this.currentModelClass.typename;
  }

  get currentScreenDefinition(): ScreenDefinition | undefined {
    return this.state.projectScreens?.screenDefinitionForType(
      this.currentModelTypename
    );
  }

  get customFields() {
    const { currentModelTypename } = this;
    if (!(currentModelTypename in this.local.customFields)) {
      const customFields =
        this.currentScreenDefinition?.customFieldDefinitions || [];

      this.local.customFields[currentModelTypename] = customFields
        .map(customField =>
          buildCustomField(customField, this.state.hideRailsRenderingOptions)
        )
        .filter(Boolean);
    }

    return this.local.customFields[currentModelTypename];
  }

  get customFieldSets() {
    const { currentModelTypename } = this;
    if (!(currentModelTypename in this.local.customFieldSets)) {
      const customFields =
        this.currentScreenDefinition?.customFieldDefinitions || [];

      this.local.customFieldSets[currentModelTypename] = customFields
        .map(customField =>
          buildCustomFieldSet(customField, this.state.hideRailsRenderingOptions)
        )
        .filter(Boolean);
    }

    return this.local.customFieldSets[currentModelTypename];
  }

  getGroup(groupId) {
    return this.currentCardView.getGroup(groupId);
  }

  getGroupForField(cardFieldId) {
    return this.currentCardView.getGroupForField(cardFieldId);
  }

  resetViewState() {
    this.actionSetSearchText('');
    this.prepareCardViewRows();
  }

  prepareCardViewRows() {
    // Remove all empty rows, then add a new one at the bottom
    this.currentCardView.removeEmptyRows();
    this.currentCardView.createRow();
  }

  actionSetSearchText(searchText) {
    const lowerSearchText = searchText.toLowerCase();

    // Filter UI items
    const visibleUiItems = UI_ITEMS.filter(uiItem =>
      uiItem.title.includes(lowerSearchText)
    );

    // Filter visible standard fields
    const visibleStandardFields = sortBy(
      Array.from(this.currentRecordFields.fieldSets.values())
        .map(fieldSet => {
          const fields = fieldSet.availableFields;

          if (fields.length === 0) {
            return;
          }

          const visibleFields = fields.filter(field =>
            field.title.toLowerCase().includes(lowerSearchText)
          );

          const showAllFields = fieldSet.title
            .toLowerCase()
            .includes(lowerSearchText);

          if (showAllFields || visibleFields.length > 0) {
            return {
              fieldSet,
              fields: showAllFields ? fields : visibleFields,
            };
          }
        })
        .filter(Boolean),
      ({ fieldSet }) => fieldSet.title
    );

    // Filter visible custom fields
    const visibleCustomFields = sortBy(
      Array.from(this.customFields)
        .map(customField => {
          if (
            (customField.title.toLowerCase().includes(lowerSearchText) ||
              customField.key.toLowerCase().includes(lowerSearchText)) &&
            customField.type !== 'UrlField' // We don't want to show the old UrlField in the sidebar, only show the set of custom fields
          ) {
            return customField;
          }
        })
        .filter(Boolean),
      customField => customField.title
    );
    // Filter visible custom fields sets
    const visibleCustomFieldSets = sortBy(
      Array.from(this.customFieldSets)
        .map(customField => {
          if (
            customField.recordFields.name
              .toLowerCase()
              .includes(lowerSearchText) ||
            customField?.key?.toLowerCase()?.includes(lowerSearchText)
          ) {
            return customField;
          }
        })
        .filter(Boolean),
      customField => customField.title
    );

    Object.assign(this.state, {
      visibleStandardFields,
      visibleCustomFields,
      visibleCustomFieldSets,
      visibleUiItems,
    });
  }

  actionToggleShowUiItems() {
    this.state.showUiItems = !this.state.showUiItems;
  }

  actionToggleShowStandardFields() {
    this.state.showStandardFields = !this.state.showStandardFields;
  }

  actionToggleShowCustomFields() {
    this.state.showCustomFields = !this.state.showCustomFields;
  }

  actionSetCardView(cardView: CardViewType) {
    this.state.activeCardView = cardView;
    this.prepareCardViewRows();
  }

  actionSetShowParentRecord(showParentRecord) {
    const { currentCardView } = this;

    currentCardView.showParentRecord = showParentRecord;
  }

  actionSetShowCoverImages(showCoverImages) {
    const { currentCardView } = this;

    currentCardView.coverImages = showCoverImages;
  }

  actionSetCardStyling(cardStylingType) {
    const { currentCardView } = this;
    let cardStyling: CardStyling | null = null;
    if (cardStylingType) {
      // For now we only have a 3 styling types: 'SidebarStatus' | 'BackgroundStatus' | 'AgingRecord'.
      cardStyling = new CardStyling(
        { type: CardStylingTypesEnum[cardStylingType].value },
        { parent: currentCardView }
      );
    }

    currentCardView.cardStyling = cardStyling;
  }

  actionResetToDefault() {
    const { defaultCardLayout } = this.local;

    const resetView =
      defaultCardLayout[this.currentRecordLayoutKey][this.cardViewKey].dup();

    resetView.inherited = true;
    this.currentRecordLayout[this.cardViewKey] = resetView;
  }

  async actionShowProjectCardLayoutEditor({ projectId }) {
    const cardViews: CardViewType[] = ALL_CARD_VIEWS;
    const project = await this.buildProjectScope({
      includeDefaultCardLayout: true,
      cardViews,
    }).find(projectId);

    if (!project) {
      showAlertMessage(
        'There was an error loading your card layout configuration.'
      );
      return;
    }

    const { projectConfiguration } = project;
    const { cardLayout, defaultCardLayout } = projectConfiguration;

    this.openCardLayoutEditor({
      cardLayout,
      defaultCardLayout,
      projectConfiguration,
      cardViews,
      async callback(cardLayout) {
        project.projectConfiguration = new ProjectConfiguration({ cardLayout });

        await project.save();
      },
    });
  }

  async actionShowProjectTemplateCardLayoutEditor({ projectTemplateId }) {
    const projectTemplate = await ProjectTemplate.select('id')
      .merge({
        projectTemplateWorkingConfiguration: ProjectConfiguration.select(
          'epicsEnabled'
        ).merge({
          cardLayout: CardLayout.buildCardLayoutScope(),
          defaultCardLayout: CardLayout.buildCardLayoutScope(),
          projectScreens: ProjectScreens.defaultScope(),
        }),
      })
      .find(projectTemplateId);

    if (!projectTemplate) {
      showAlertMessage(
        'There was an error loading your card layout configuration.'
      );
      return;
    }

    const { projectTemplateWorkingConfiguration: projectConfiguration } =
      projectTemplate;
    const { cardLayout, defaultCardLayout } = projectConfiguration;

    this.openCardLayoutEditor({
      cardLayout,
      defaultCardLayout,
      projectConfiguration,
      async callback(cardLayout) {
        projectTemplate.projectTemplateWorkingConfiguration =
          new ProjectConfiguration({ cardLayout });

        await projectTemplate.save();
      },
    });
  }

  async actionShowWhiteboardCardLayoutEditor({
    callback,
    cardLayout,
    visibleRecordTypes,
    currentRecordType = visibleRecordTypes[0],
    whiteboardId,
  }: Pick<
    WhiteboardCardLayoutEditorOptions,
    'callback' | 'cardLayout' | 'visibleRecordTypes' | 'currentRecordType'
  > & {
    callback?: CardLayoutEditorControllerLocalState['callback'];
    cardLayout: CardLayout;
    whiteboardId: RecordId;
  }) {
    const cardViews: CardViewType[] = ['whiteboard'];

    const whiteboard = await Whiteboard.merge({
      defaultCardLayout: CardLayout.buildCardLayoutScope({
        recordTypes: visibleRecordTypes,
        cardViews,
      }),
      project: Project.merge({
        projectConfiguration: ProjectConfiguration.select('epicsEnabled').merge(
          {
            projectScreens: ProjectScreens.defaultScope(),
          }
        ),
      }),
    }).find(whiteboardId);

    if (!whiteboard) {
      showAlertMessage(
        'There was an error loading your card layout configuration.'
      );
      return;
    }

    const { defaultCardLayout } = whiteboard;

    AhaAnalytics.track(
      'Feature-6987006763124540244',
      'User views card layout modal',
      {
        Source: 'Whiteboard',
      }
    );

    cardLayout.setAttribute(
      'epicsEnabled',
      defaultCardLayout.epicsEnabled,
      false
    );

    this.openCardLayoutEditor({
      callback,
      cardLayout,
      cardViews,
      defaultCardLayout,
      currentRecordType,
      visibleRecordTypes,
      projectConfiguration: whiteboard.project?.projectConfiguration,
    });
  }

  async actionShowBookmarkCardLayoutEditor({
    bookmark,
    bookmarkId,
    callback,
    cardLayout = null,
    cardViews = ['expanded', 'collapsed'],
    currentRecordType = null,
    defaultCardLayout = null,
    hideRailsRenderingOptions = false,
    hideUnsupportedCustomFields,
    initialCardView = cardViews[0],
    projectId,
    visibleRecordTypes,
  }: BookmarkLayoutEditorSettings) {
    if (bookmark) {
      cardLayout = cardLayout || bookmark.cardLayout;
      defaultCardLayout = defaultCardLayout || bookmark.defaultCardLayout;
    }

    if (!bookmark || !cardLayout || !defaultCardLayout) {
      if (!bookmarkId) {
        throw new Error(
          'Cannot load bookmark/card layout without a bookmarkId'
        );
      }

      const bookmark = await Bookmark.merge({
        cardLayout: CardLayout.buildCardLayoutScope(),
        defaultCardLayout: CardLayout.buildCardLayoutScope(),
      }).find(bookmarkId);

      if (!bookmark) {
        showAlertMessage(
          'There was an error loading your card layout configuration.'
        );
        return;
      }

      ({ cardLayout, defaultCardLayout } = bookmark);
    }

    const project = await this.buildProjectScope({
      includeCardLayout: !cardLayout,
      includeDefaultCardLayout: !defaultCardLayout,
    }).find(projectId);

    if (!project) {
      showAlertMessage(
        'There was an error loading your card layout configuration.'
      );
      return;
    }

    const { projectConfiguration } = project;
    cardLayout = cardLayout || projectConfiguration.cardLayout;
    defaultCardLayout =
      defaultCardLayout || projectConfiguration.defaultCardLayout;

    this.openCardLayoutEditor({
      async callback(cardLayout) {
        if (bookmarkId) {
          const bookmark = new Bookmark({ id: bookmarkId });
          bookmark.cardLayout = cardLayout;

          await bookmark.save();
        }

        if (callback) {
          callback(cardLayout);
        }
      },
      cardLayout,
      defaultCardLayout,
      currentRecordType,
      hideRailsRenderingOptions,
      initialCardView,
      projectConfiguration,
      visibleRecordTypes,
      cardViews,
      hideUnsupportedCustomFields,
    });
  }

  buildProjectScope({
    includeCardLayout = true,
    includeDefaultCardLayout = false,
    cardViews = DEFAULT_CARD_VIEWS,
  }: {
    includeCardLayout?: boolean;
    includeDefaultCardLayout?: boolean;
    cardViews?: CardViewType[];
  }) {
    const configurationScopes: Subqueries = {
      projectScreens: ProjectScreens.defaultScope(),
    };

    if (includeCardLayout) {
      configurationScopes.cardLayout = CardLayout.buildCardLayoutScope({
        cardViews,
      });
    }

    if (includeDefaultCardLayout) {
      configurationScopes.defaultCardLayout = CardLayout.buildCardLayoutScope({
        cardViews,
      });
    }

    return Project.select('id', 'name', 'isTeam').merge({
      projectConfiguration:
        ProjectConfiguration.select('epicsEnabled').merge(configurationScopes),
    });
  }

  openCardLayoutEditor({
    callback,
    cardLayout,
    cardViews = DEFAULT_CARD_VIEWS,
    currentRecordType = null,
    defaultCardLayout,
    hideRailsRenderingOptions = false,
    initialCardView = cardViews[0],
    projectConfiguration,
    visibleRecordTypes,
  }: Omit<BookmarkLayoutEditorSettings, 'projectId'> & {
    projectConfiguration?: ProjectConfiguration;
  }) {
    const { projectScreens } = projectConfiguration || {};

    Object.assign(this.local, { callback, defaultCardLayout });

    const allRecordLayouts = Object.keys(cardLayout.availableRecordLayouts());
    const availableRecordLayouts = visibleRecordTypes
      ? visibleRecordTypes.map(recordType =>
          CardLayout.recordLayoutKey(recordType)
        )
      : allRecordLayouts;

    // Convert currentRecordType to currentRecordLayoutKey
    let currentRecordLayoutKey: CardLayoutEditorControllerState['currentRecordLayoutKey'] =
      null;
    if (currentRecordType) {
      currentRecordLayoutKey = CardLayout.recordLayoutKey(currentRecordType);
    }

    // Ensure the currentRecordLayoutKey is valid
    if (
      currentRecordLayoutKey &&
      !availableRecordLayouts.includes(currentRecordLayoutKey)
    ) {
      currentRecordLayoutKey = null;
    }

    Object.assign(this.state, {
      availableRecordLayouts,
      currentRecordLayoutKey,
      cardLayout: CardLayout.buildActiveCardLayout({
        cardLayout,
        defaultCardLayout,
        cardViews,
        recordTypes: visibleRecordTypes,
      }),
      hideRailsRenderingOptions,
      open: true,
      projectScreens,
      saving: false,
      activeCardView: initialCardView,
      cardViews,
    } as Partial<CardLayoutEditorControllerState>);

    sharedSelectionController.reset();

    this.resetViewState();

    this.renderGlobalContainer();
  }

  actionSetRecordLayout(recordLayoutKey) {
    this.state.currentRecordLayoutKey = recordLayoutKey;

    this.resetViewState();
  }

  async actionSave() {
    this.state.saving = true;

    const { callback } = this.local;
    const { cardLayout } = this.state;

    AhaAnalytics.track(
      'Feature-6987006763124540244',
      'User saves card layout',
      {}
    );

    await callback(cardLayout);

    this.actionCloseModal();
  }

  actionCloseModal() {
    this.state.open = false;
  }

  actionDropDividerRow({ items, placement }) {
    const cardView = this.currentCardView;

    // Are we dropping an existing row?
    if (items[0] instanceof CardRow) {
      cardView.placeRow(items[0], placement);
    } else {
      cardView.createRow(placement, true);
    }
  }

  actionDropFieldInCardGroup({ groupId, field, cardField, placement }) {
    const newCardGroup = this.getGroup(groupId);
    if (!newCardGroup)
      throw new Error(
        `Could not find group to add field to, groupId: ${groupId}`
      );

    // Move existing field, or create a new one
    if (cardField) {
      const currentCardGroup = this.getGroupForField(cardField.id);

      currentCardGroup.removeField(cardField.id);
    } else if (field instanceof CustomField) {
      const { customFieldDefinition, cardFieldType } = field;
      const key =
        field.customFieldDefinition.constructor.prototype instanceof FieldSet
          ? // @ts-ignore
            customFieldDefinition.recordFields.id + '_' + field.constructor.name
          : field.id;

      const defintion =
        field.customFieldDefinition.constructor.prototype instanceof FieldSet
          ? // @ts-ignore
            customFieldDefinition.recordFields
          : customFieldDefinition;
      cardField = new CardField(
        {
          id: randomId(),
          type: cardFieldType,
          key: key,
          customFieldDefinition: defintion,
        },
        { parent: newCardGroup }
      );

      AhaAnalytics.track(
        'Feature-6987006763124540244',
        'User adds field to card layout',
        {
          'Custom field': true,
          'Field type': customFieldDefinition.type,
        }
      );
    } else {
      cardField = new CardField(
        {
          id: randomId(),
          type: CardFieldTypesEnum.STANDARD_FIELD.value,
          key: field.key,
        },
        { parent: newCardGroup }
      );

      AhaAnalytics.track(
        'Feature-6987006763124540244',
        'User adds field to card layout',
        {
          'Custom field': false,
          'Field name': field.key,
        }
      );

      if (['RiskTag', 'RiskWarningIcon'].includes(field.key)) {
        AhaAnalytics.track(
          'Feature-7148130073796750739',
          'User adds delivery risks to card layout',
          {
            'Workspace type': window?.currentWorkspaceType,
            'Record type': this.currentRecordLayout.modelClass.typename,
            'Field name': field.key,
          }
        );
      }
    }

    newCardGroup.addField(cardField, placement);

    this.actionStopDrag();
  }

  actionRemoveDividerRow({ dividerRow }) {
    const { currentCardView } = this;
    currentCardView.removeRow(dividerRow);
  }

  actionRemoveCardField({ cardField }) {
    if (cardField) {
      this.currentCardView.removeCardField(cardField);

      const { field } = cardField;

      if (field instanceof CustomField) {
        AhaAnalytics.track(
          'Feature-6987006763124540244',
          'User removes field from card layout',
          {
            'Custom field': true,
            'Field type': field.customFieldDefinition?.type,
          }
        );
      } else if (field) {
        AhaAnalytics.track(
          'Feature-6987006763124540244',
          'User removes field from card layout',
          {
            'Custom field': false,
            'Field name': field.key,
          }
        );
      }
    }

    this.actionStopDrag();
  }

  actionStopDrag() {
    this.prepareCardViewRows();
  }
}

export const sharedCardLayoutController = new CardLayoutEditorController();
sharedCardLayoutController.internalInitialize(null, {});

export default CardLayoutEditorController;
